import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Real-time listener for current user's support tickets
 */
export const useMySupportTickets = (uid) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setTickets([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'support_tickets'),
      where('creatorUid', '==', uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching support tickets:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [uid]);

  return { tickets, loading };
};

/**
 * Real-time listener for a single support ticket
 */
export const useSupportTicket = (ticketId) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticketId) return;

    const docRef = doc(db, 'support_tickets', ticketId);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setTicket({ id: docSnap.id, ...docSnap.data() });
        } else {
          setTicket(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching support ticket:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [ticketId]);

  return { ticket, loading };
};

/**
 * Real-time listener for messages in a support ticket
 */
export const useTicketMessages = (ticketId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticketId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'support_tickets', ticketId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching ticket messages:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [ticketId]);

  return { messages, loading };
};

/**
 * Create a new support ticket (enforces max 2 open tickets)
 */
export const createSupportTicket = async ({ subject, message, uid, username }) => {
  // Check open ticket count
  const openQuery = query(
    collection(db, 'support_tickets'),
    where('creatorUid', '==', uid),
    where('status', '==', 'open')
  );
  const openSnap = await getDocs(openQuery);
  if (openSnap.size >= 2) {
    throw new Error('You can only have 2 open support tickets at a time.');
  }

  const ticketRef = await addDoc(collection(db, 'support_tickets'), {
    creatorUid: uid,
    creatorUsername: username,
    subject,
    message,
    status: 'open',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return ticketRef.id;
};

/**
 * Add a reply message to a support ticket
 */
export const addTicketMessage = async (ticketId, { senderUid, senderName, isAdmin, message }) => {
  await addDoc(collection(db, 'support_tickets', ticketId, 'messages'), {
    senderUid,
    senderName,
    isAdmin: isAdmin || false,
    message,
    createdAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'support_tickets', ticketId), {
    updatedAt: serverTimestamp(),
  });
};
